import React, { useState } from 'react';
import { Check, Copy, ExternalLink, Link2, Loader2, MessageCircle } from 'lucide-react';
import styles from './ReportShareBox.module.css';
import { SMIIFY_API } from '../../config/api';
import { longDate } from '../../utils/money';

/** Creates (or reuses) the read-only link for one day's report and offers copy / WhatsApp / open */
export const createReportLink = async ({ storeId, date }) => {
    const res = await fetch(`${SMIIFY_API}/daily-report/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId },
        body: JSON.stringify({ date }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) throw new Error(json.message || 'Could not create the link.');
    return json.data; // { url, token, expiresAt }
};

export default function ReportShareBox({ storeId, date, storeName }) {
    const [link, setLink] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    const make = async () => {
        setBusy(true); setError('');
        try { setLink(await createReportLink({ storeId, date })); } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setBusy(false); }
    };
    const copy = async () => {
        try { await navigator.clipboard.writeText(link.url); } catch (e) { window.prompt('Copy this link', link.url); }
        setCopied(true); setTimeout(() => setCopied(false), 1800);
    };
    const waText = link ? `Daily closing report, ${storeName || 'our branch'}, ${longDate(date)}:\n${link.url}` : '';

    if (!link) {
        return (
            <div className={styles.box}>
                <div className={styles.copy}><Link2 size={15} /><span><strong>Share this report</strong><small>Anyone with the link can view this day's report. No login needed. Client phone numbers stay hidden.</small></span></div>
                <button type="button" className={styles.primary} onClick={make} disabled={busy || !storeId}>{busy ? <Loader2 size={14} className={styles.spin} /> : <Link2 size={14} />} Create link</button>
                {error && <p className={styles.error}>{error}</p>}
            </div>
        );
    }
    return (
        <div className={styles.box}>
            <div className={styles.linkRow}>
                <input readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Report link" />
                <button type="button" onClick={copy} className={styles.ghost}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div className={styles.actions}>
                <a className={styles.wa} href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer"><MessageCircle size={14} /> Share on WhatsApp</a>
                <a className={styles.ghost} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open</a>
            </div>
            <small className={styles.valid}>Link works till {new Date(link.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</small>
        </div>
    );
}
